import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { useRealtimeStore, type RealtimeStatus } from '@repletoryreach/sdk-utils/store';
import type { HubEventName, HubEventPayload, HubMap, HubName } from './events.js';

export interface RealtimeOptions {
  hubUrl: string;
  accessToken: () => string | null;
  verbose?: boolean;
  syncStore?: boolean;
}

function mapState(state: HubConnectionState | undefined): RealtimeStatus {
  switch (state) {
    case HubConnectionState.Connected:    return 'connected';
    case HubConnectionState.Connecting:   return 'connecting';
    case HubConnectionState.Reconnecting: return 'reconnecting';
    default:                              return 'disconnected';
  }
}

type Handler = (payload: unknown) => void;

export class RealtimeClient<H extends HubName = HubName> {
  private connection: HubConnection | null = null;
  private readonly handlers = new Map<string, Set<Handler>>();
  private readonly syncStore: boolean;

  constructor(private readonly opts: RealtimeOptions) {
    this.syncStore = opts.syncStore !== false;
  }

  private writeStatus(status: RealtimeStatus, error?: Error): void {
    if (!this.syncStore) return;
    const store = useRealtimeStore.getState();
    store.setStatus(status);
    if (error) store.noteError(error);
  }

  async start(): Promise<void> {
    if (this.connection && this.connection.state === HubConnectionState.Connected) return;
    this.writeStatus('connecting');
    if (this.syncStore) useRealtimeStore.getState().noteAttempt();

    this.connection = new HubConnectionBuilder()
      .withUrl(this.opts.hubUrl, {
        accessTokenFactory: () => this.opts.accessToken() ?? ''
      })
      .withAutomaticReconnect()
      .configureLogging(this.opts.verbose ? LogLevel.Information : LogLevel.Warning)
      .build();

    this.connection.onreconnecting((e) => this.writeStatus('reconnecting', e));
    this.connection.onreconnected(() => this.writeStatus('connected'));
    this.connection.onclose((e) => this.writeStatus('disconnected', e));

    for (const [event, set] of this.handlers) {
      this.connection.on(event, (payload: unknown) => set.forEach((h) => h(payload)));
    }

    try {
      await this.connection.start();
      this.writeStatus('connected');
    } catch (e) {
      this.writeStatus('disconnected', e instanceof Error ? e : new Error(String(e)));
      throw e;
    }
  }

  on<E extends HubEventName<H>>(event: E, handler: (payload: HubEventPayload<H, E>) => void): () => void {
    const eventKey = event as string;
    let set = this.handlers.get(eventKey);
    if (!set) {
      set = new Set();
      this.handlers.set(eventKey, set);
    }
    const wrapped: Handler = (p) => handler(p as HubEventPayload<H, E>);
    set.add(wrapped);
    this.connection?.on(eventKey, wrapped);
    return () => {
      set!.delete(wrapped);
      this.connection?.off(eventKey, wrapped);
    };
  }

  async stop(): Promise<void> {
    await this.connection?.stop();
    this.connection = null;
    this.writeStatus('disconnected');
  }

  get state(): RealtimeStatus {
    return mapState(this.connection?.state);
  }
}

export type { HubMap, HubName, HubEventName, HubEventPayload };

/**
 * W6: factory for typed hub clients. The `hub` name narrows the returned
 * client's event surface: a `createHub('broadcasts', …)` client's `on()`
 * only accepts events from `BroadcastEvents`.
 *
 * `getInboxClient()` stays as the singleton accessor for the inbox/templates
 * hub; this factory is for the additional hubs (broadcasts, flows) that
 * land in W11/W12 without disturbing the inbox path.
 */
export function createHub<H extends HubName>(
  hub: H,
  options: RealtimeOptions
): RealtimeClient<H> {
  void hub; // type-only: narrows the returned client's event surface.
  return new RealtimeClient<H>(options);
}
